import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const empty = { title: "", slug: "", excerpt: "", body: "", author: "Taxfree Filer Editorial", tags: "" };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/blog");
    setPosts(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.slug);
    setForm({ ...p, tags: (p.tags || []).join(", ") });
    setOpen(true);
  };

  const submit = async () => {
    const payload = { ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) };
    try {
      if (editing) {
        const { slug, ...rest } = payload;
        await api.put(`/blog/${editing}`, rest);
        toast.success("Post updated");
      } else {
        await api.post("/blog", payload);
        toast.success("Post created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = (post) => {
    setToDelete(post);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/blog/${toDelete.slug}`);
      toast.success("Post deleted");
      setToDelete(null);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="blog-admin-title">Blog CMS</h1>
          <p className="text-slate-600 mt-1">Create, edit and archive editorial posts.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="new-post-btn"><Plus size={16} className="mr-1" /> New post</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="post-title" /></div>
              <div>
                <Label>Slug (URL-friendly)</Label>
                <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} disabled={!!editing} data-testid="post-slug" />
              </div>
              <div><Label>Excerpt</Label><Textarea rows={2} value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} data-testid="post-excerpt" /></div>
              <div><Label>Body</Label><Textarea rows={10} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} data-testid="post-body" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Author</Label><Input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} /></div>
                <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} data-testid="post-tags" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-[#0A2540]" onClick={submit} data-testid="post-submit">{editing ? "Save changes" : "Publish"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border tf-hairline bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="blog-admin-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr><th className="text-left p-3">Title</th><th className="text-left p-3">Slug</th><th className="text-left p-3">Author</th><th className="text-left p-3">Tags</th><th /></tr>
          </thead>
          <tbody>
            {posts.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No posts yet.</td></tr>}
            {posts.map(p => (
              <tr key={p.slug} className="border-t tf-hairline hover:bg-slate-50">
                <td className="p-3 font-semibold text-[#0A2540]">{p.title}</td>
                <td className="p-3 font-mono text-xs text-slate-500">{p.slug}</td>
                <td className="p-3 text-slate-600">{p.author}</td>
                <td className="p-3 text-xs">{(p.tags || []).join(", ")}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-post-${p.slug}`}><PencilSimple size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`delete-post-${p.slug}`}><Trash size={14} className="text-[#D32F2F]" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete post?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && `"${toDelete.title}"`}. This permanently removes the post from the blog. Backlinks to /blog/{toDelete?.slug} will 404. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-post-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="delete-post-confirm">
              Delete post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
